import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '../lib/api';
import ImageInput from './ImageInput';
import { TextField, TextArea, SelectField } from './AdminFields';

const Block = ({ title, hint, children, testid }) => (
    <div className="border border-white/10 p-6 space-y-4" data-testid={testid}>
        <div>
            <h3 className="font-display font-black uppercase text-xl">{title}</h3>
            {hint && <p className="text-white/50 text-sm mt-1">{hint}</p>}
        </div>
        {children}
    </div>
);

// Generic editable list: render(item, update) per row, with add / reorder / delete.
const ListEditor = ({ items, setItems, blank, render, testid, addLabel = 'Add' }) => {
    const upd = (i, patch) => setItems(items.map((it, idx) => (idx === i ? (typeof it === 'string' ? patch : { ...it, ...patch }) : it)));
    const move = (i, d) => {
        const j = i + d;
        if (j < 0 || j >= items.length) return;
        const next = [...items];
        [next[i], next[j]] = [next[j], next[i]];
        setItems(next);
    };
    return (
        <div className="space-y-3">
            {items.map((it, i) => (
                <div key={i} className="border border-white/10 bg-brand-bg p-4 space-y-3" data-testid={`${testid}-row-${i}`}>
                    <div className="flex justify-end gap-1">
                        <button type="button" onClick={() => move(i, -1)} className="p-1.5 border border-white/10 hover:bg-white/10"><ArrowUp className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => move(i, 1)} className="p-1.5 border border-white/10 hover:bg-white/10"><ArrowDown className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => setItems(items.filter((_, idx) => idx !== i))} className="p-1.5 border border-red-500/60 text-red-600 hover:bg-red-500/10" data-testid={`${testid}-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    {render(it, (patch) => upd(i, patch), i)}
                </div>
            ))}
            <button type="button" onClick={() => setItems([...items, typeof blank === 'function' ? blank() : blank])}
                className="inline-flex items-center gap-1 label-caps text-brand-primary text-xs" data-testid={`${testid}-add`}>
                <Plus className="h-4 w-4" /> {addLabel}
            </button>
        </div>
    );
};

const slug = () => `course-${Math.random().toString(36).slice(2, 8)}`;

const PagesAdmin = () => {
    const [c, setC] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [catalog, setCatalog] = useState([]);
    useEffect(() => {
        api.get('/page-content').then((r) => setC(r.data));
        api.get('/admin/machines').then((r) => setCatalog(r.data)).catch(() => api.get('/machines').then((r) => setCatalog(r.data)));
    }, []);
    if (!c) return <div className="text-white/50 label-caps">Loading…</div>;

    const set = (k) => (v) => setC({ ...c, [k]: v });
    const story = c.ken_story;
    const setStory = (patch) => setC({ ...c, ken_story: { ...story, ...patch } });

    const save = async () => {
        setSaving(true); setMsg('');
        try {
            await api.put('/admin/page-content', {
                ...c,
                ken_story: { ...story, photos: (story.photos || []).filter(Boolean) },
                partner_requirements: c.partner_requirements.filter((s) => s.trim()),
                rental_terms: c.rental_terms.filter((s) => s.trim()),
                courses: c.courses,
            });
            setMsg('Saved — live on the site now.');
        } catch (e) {
            setMsg('Could not save — check numbers and required fields.');
        }
        setSaving(false);
    };

    return (
        <div className="max-w-3xl space-y-8" data-testid="admin-pages">
            <div className="flex items-center justify-between gap-4 sticky top-0 bg-brand-bg py-3 z-10">
                <h2 className="font-display font-black uppercase text-3xl">Pages</h2>
                <div className="flex items-center gap-3">
                    {msg && <span className="text-sm text-white/60" data-testid="pages-msg">{msg}</span>}
                    <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover text-brand-ink font-bold px-5 py-3 label-caps" data-testid="pages-save">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all
                    </button>
                </div>
            </div>
            <p className="text-white/50 text-sm">Text starting with [SAMPLE …] is placeholder — replace it with your real content.</p>

            <Block title="About — Ken's story" testid="pages-ken">
                <div className="grid grid-cols-2 gap-4">
                    <TextField label="Small label" value={story.eyebrow} onChange={(v) => setStory({ eyebrow: v })} testid="pages-ken-eyebrow" />
                    <TextField label="Heading" value={story.heading} onChange={(v) => setStory({ heading: v })} testid="pages-ken-heading" />
                </div>
                <TextArea label="Story (blank line = new paragraph)" rows={8} value={story.body} onChange={(v) => setStory({ body: v })} testid="pages-ken-body" />
                <TextField label="Quote from Ken (optional)" value={story.quote} onChange={(v) => setStory({ quote: v })} testid="pages-ken-quote" />
                <div className="label-caps text-white/50 text-xs">Photos (first one shows large)</div>
                <ListEditor items={story.photos || []} setItems={(v) => setStory({ photos: v })} blank="" testid="pages-ken-photos" addLabel="Add photo"
                    render={(p, u, i) => <ImageInput label={`Photo ${i + 1}`} value={p} onChange={u} testid={`pages-ken-photo-${i}`} />} />
            </Block>

            <Block title="About — Timeline" testid="pages-timeline">
                <ListEditor items={c.timeline} setItems={set('timeline')} blank={{ year: '', title: '', text: '', image_url: '' }} testid="pages-timeline" addLabel="Add milestone"
                    render={(t, u, i) => (
                        <>
                            <div className="grid grid-cols-3 gap-3">
                                <TextField label="Year" value={t.year} onChange={(v) => u({ year: v })} testid={`pages-timeline-year-${i}`} />
                                <div className="col-span-2"><TextField label="Title" value={t.title} onChange={(v) => u({ title: v })} testid={`pages-timeline-title-${i}`} /></div>
                            </div>
                            <TextArea label="Text" rows={2} value={t.text} onChange={(v) => u({ text: v })} testid={`pages-timeline-text-${i}`} />
                            <ImageInput label="Photo (optional)" value={t.image_url} onChange={(v) => u({ image_url: v })} testid={`pages-timeline-image-${i}`} />
                        </>
                    )} />
            </Block>

            <Block title="Partner Program page" testid="pages-partners">
                <TextArea label="Intro" rows={3} value={c.partner_intro} onChange={set('partner_intro')} testid="pages-partner-intro" />
                <div className="label-caps text-white/50 text-xs">Benefits</div>
                <ListEditor items={c.partner_benefits} setItems={set('partner_benefits')} blank={{ title: '', text: '' }} testid="pages-benefits" addLabel="Add benefit"
                    render={(b, u, i) => (
                        <>
                            <TextField label="Title" value={b.title} onChange={(v) => u({ title: v })} testid={`pages-benefit-title-${i}`} />
                            <TextArea label="Text" rows={2} value={b.text} onChange={(v) => u({ text: v })} testid={`pages-benefit-text-${i}`} />
                        </>
                    )} />
                <div className="label-caps text-white/50 text-xs">Requirements</div>
                <ListEditor items={c.partner_requirements} setItems={set('partner_requirements')} blank="" testid="pages-requirements" addLabel="Add requirement"
                    render={(r, u, i) => <TextField label={`Requirement ${i + 1}`} value={r} onChange={u} testid={`pages-requirement-${i}`} />} />
            </Block>

            <Block title="Machine Finder" hint="Pick the machine and nozzle each result recommends. Leave the machine blank to auto-pick from the shop category (Detailing/Light vs Industrial Performance)." testid="pages-finder">
                {['light', 'industrial'].map((tier) => {
                    const p = (c.finder || {})[tier] || {};
                    const setP = (patch) => setC({ ...c, finder: { ...(c.finder || {}), [tier]: { ...p, ...patch } } });
                    return (
                        <div key={tier} className="border border-white/10 bg-brand-bg p-4 space-y-3" data-testid={`pages-finder-${tier}`}>
                            <div className="label-caps text-brand-primary text-xs">{tier === 'light' ? 'Compact / light-industrial result' : 'Industrial performance result'}</div>
                            <div className="grid grid-cols-2 gap-3">
                                <SelectField label="Machine" value={p.machine_id || ''} onChange={(v) => setP({ machine_id: v })} testid={`pages-finder-${tier}-machine`}
                                    options={[['', 'Auto (from category)'], ...catalog.filter((m) => m.machine_type === 'machine').map((m) => [m.id, m.name])]} />
                                <SelectField label="Nozzle / accessory" value={p.nozzle_id || ''} onChange={(v) => setP({ nozzle_id: v })} testid={`pages-finder-${tier}-nozzle`}
                                    options={[['', 'Auto (first nozzle)'], ...catalog.filter((m) => m.machine_type === 'accessory').map((m) => [m.id, m.name])]} />
                            </div>
                            <TextArea label="Why this setup" rows={2} value={p.note || ''} onChange={(v) => setP({ note: v })} testid={`pages-finder-${tier}-note`} />
                        </div>
                    );
                })}
            </Block>

            <Block title="FAQ page" testid="pages-faq">
                <ListEditor items={c.faqs || []} setItems={set('faqs')} blank={{ q: '', a: '' }} testid="pages-faqs" addLabel="Add question"
                    render={(f, u, i) => (
                        <>
                            <TextField label="Question" value={f.q} onChange={(v) => u({ q: v })} testid={`pages-faq-q-${i}`} />
                            <TextArea label="Answer" rows={3} value={f.a} onChange={(v) => u({ a: v })} testid={`pages-faq-a-${i}`} />
                        </>
                    )} />
            </Block>

            <Block title="Service & Support page" testid="pages-support">
                <TextArea label="Intro" rows={3} value={c.support_intro} onChange={set('support_intro')} testid="pages-support-intro" />
                <TextField label="Support line hours" value={c.support_hours} onChange={set('support_hours')} testid="pages-support-hours" />
                <div className="label-caps text-white/50 text-xs">Service cards (3rd card links to parts, 4th to Knowledge center)</div>
                <ListEditor items={c.support_services || []} setItems={set('support_services')} blank={{ title: '', text: '' }} testid="pages-support-services" addLabel="Add card"
                    render={(b, u, i) => (
                        <>
                            <TextField label="Title" value={b.title} onChange={(v) => u({ title: v })} testid={`pages-support-title-${i}`} />
                            <TextArea label="Text" rows={2} value={b.text} onChange={(v) => u({ text: v })} testid={`pages-support-text-${i}`} />
                        </>
                    )} />
            </Block>

            <Block title="Rental page" hint="Which machines are rentable and their daily/weekly rates are set on each machine (Machines tab → Rental)." testid="pages-rental">
                <TextArea label="Intro" rows={3} value={c.rental_intro} onChange={set('rental_intro')} testid="pages-rental-intro" />
                <div className="label-caps text-white/50 text-xs">Rental terms</div>
                <ListEditor items={c.rental_terms} setItems={set('rental_terms')} blank="" testid="pages-terms" addLabel="Add term"
                    render={(t, u, i) => <TextField label={`Term ${i + 1}`} value={t} onChange={u} testid={`pages-term-${i}`} />} />
            </Block>

            <Block title="Training page" hint="Programs are shown without prices — every training is quoted to the customer's needs. Requests arrive in the Training tab and Inbox." testid="pages-training">
                <TextArea label="Intro" rows={3} value={c.training_intro || ''} onChange={set('training_intro')} testid="pages-training-intro" />
                <div className="label-caps text-white/50 text-xs">Training programs</div>
                <ListEditor items={c.courses} setItems={set('courses')} blank={() => ({ id: slug(), name: '', duration: '', description: '' })} testid="pages-courses" addLabel="Add program"
                    render={(x, u, i) => (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <TextField label="Program name" value={x.name} onChange={(v) => u({ name: v })} testid={`pages-course-name-${i}`} />
                                <TextField label="Typical duration" value={x.duration} onChange={(v) => u({ duration: v })} testid={`pages-course-duration-${i}`} />
                            </div>
                            <TextArea label="Description" rows={2} value={x.description} onChange={(v) => u({ description: v })} testid={`pages-course-desc-${i}`} />
                        </>
                    )} />
            </Block>
        </div>
    );
};

export default PagesAdmin;
